import { useState, useCallback, useEffect } from 'react';
import { useBlocker } from '@tanstack/react-router';

/**
 * Unified navigation guard: blocks in-app route changes (useBlocker),
 * browser close/refresh (beforeunload), and manages the discard dialog state.
 *
 * Usage:
 *   const guard = useNavigationGuard(isDirty, goBack)
 *   useEscapeKey(guard.promptOrLeave)
 *   <UnsavedChangesDialog {...guard.dialogProps} />
 */

interface UseNavigationGuardResult {
  /** Call on Escape/Cancel — shows dialog if dirty, otherwise calls onLeave */
  promptOrLeave: () => void;
  /** Props to spread on UnsavedChangesDialog */
  dialogProps: {
    open: boolean;
    onDiscard: () => void;
    onCancel: () => void;
  };
}

export function useNavigationGuard(
  isDirty: boolean,
  onLeave: () => void
): UseNavigationGuardResult {
  const [showDialog, setShowDialog] = useState(false);

  // Block in-app route navigation when dirty
  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    withResolver: true,
  });

  // Block browser close/refresh when dirty
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const promptOrLeave = useCallback((): void => {
    if (isDirty) {
      setShowDialog(true);
    } else {
      onLeave();
    }
  }, [isDirty, onLeave]);

  const isBlocked = blocker.status === 'blocked';

  const handleDiscard = useCallback((): void => {
    if (isBlocked) {
      blocker.proceed();
    } else {
      setShowDialog(false);
      onLeave();
    }
  }, [isBlocked, blocker, onLeave]);

  const handleCancel = useCallback((): void => {
    if (isBlocked) {
      blocker.reset();
    } else {
      setShowDialog(false);
    }
  }, [isBlocked, blocker]);

  return {
    promptOrLeave,
    dialogProps: {
      open: showDialog || isBlocked,
      onDiscard: handleDiscard,
      onCancel: handleCancel,
    },
  };
}
